import React, { useEffect, useState } from "react";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import KpiCard from "../shared/KpiCard";
import { Eye, Heart, MessageSquare, LayoutGrid, Phone, MessageCircle, TrendingUp, Star } from "lucide-react";
import { EmptyState } from "./DashListings";

export default function DashAnalytics({ businessId }) {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get(`/platform/dashboard/business/${businessId}/analytics`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [businessId]);

  if (loading) return <div className="text-[#8a8a8a]">…</div>;
  if (!data) return <EmptyState labelAr="لا توجد بيانات" labelEn="No data" />;
  const k = data.kpis || {}; const l = k.listings || {}; const e = k.engagement || {};
  const months = data.monthly_series || [];
  const maxViews = Math.max(1, ...months.map((m) => m.views || 0));

  return (
    <div className="space-y-8" data-testid="dash-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testid="ana-listings" icon={LayoutGrid} labelAr="إجمالي الإعلانات" labelEn="Total listings" value={l.total} />
        <KpiCard testid="ana-views" icon={Eye} labelAr="المشاهدات" labelEn="Views" value={e.views_total} tone="accent" />
        <KpiCard testid="ana-favs" icon={Heart} labelAr="مفضلة" labelEn="Favorites" value={e.favorites_total} />
        <KpiCard testid="ana-messages" icon={MessageSquare} labelAr="الرسائل" labelEn="Messages" value={e.messages_total} />
        <KpiCard testid="ana-phone" icon={Phone} labelAr="نقرات الهاتف" labelEn="Phone clicks" value={e.phone_clicks_30d} tone="soft" />
        <KpiCard testid="ana-whats" icon={MessageCircle} labelAr="واتساب" labelEn="WhatsApp" value={e.whatsapp_clicks_30d} tone="soft" />
        <KpiCard testid="ana-leads" icon={TrendingUp} labelAr="عملاء محتملون" labelEn="Leads" value={e.leads_30d} tone="soft" />
        <KpiCard testid="ana-featured" icon={Star} labelAr="مميّز" labelEn="Featured" value={l.featured} tone="soft" />
      </div>

      {/* Simple bar chart */}
      <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="ana-monthly-chart">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e]">
            {lang === "ar" ? "المشاهدات الشهرية (آخر 12 شهر)" : "Monthly views (last 12 months)"}
          </div>
        </div>
        <div className="flex items-end gap-1.5 h-40">
          {months.map((m) => {
            const h = Math.max(4, Math.round((m.views / maxViews) * 100));
            return (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: ${m.views} views`}>
                <div className="w-full rounded-t-md" style={{ height: `${h}%`, background: "linear-gradient(180deg,#e8c873,#8a6f2e)" }} />
                <div className="text-[9px] text-[#8a8a8a] font-mono">{m.month.slice(5)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top listings */}
      {data.top_listings?.length > 0 && (
        <div className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5" data-testid="ana-top">
          <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-4">
            {lang === "ar" ? "الأعلى أداءً" : "Top performers"}
          </div>
          <div className="divide-y divide-[#0a0a0a]/[0.06]">
            {data.top_listings.map((v, i) => (
              <div key={v.id} className="flex items-center gap-3 py-2.5">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: i === 0 ? "#c9a55c" : "#f0eee7", color: i === 0 ? "#fff" : "#525252" }}>{i + 1}</div>
                <div className="flex-1 text-sm truncate">
                  {v.title || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim()}
                </div>
                <div className="text-sm font-bold">{(v.view_count || 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
